import React, { useCallback, useEffect, useState } from 'react';
import { Search, ShieldOff, Loader2, Trash2, Save, Laptop, Smartphone, HelpCircle, Lock, Unlock, MapPinOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { sessionAPI, accountLockAPI } from '../services/api';
import { getErrorMessage } from '../lib/utils';

// Mirrors models_sessions.DEVICE_KINDS — an unknown kind still renders.
const KIND_ICON = { mobile: Smartphone, desktop: Laptop, unknown: HelpCircle };

const fmt = (value) => (value ? new Date(value).toLocaleString() : '—');

// Mirrors models_attendance_integrity lock reasons. One today; the shape allows
// the next one without touching the rendering.
const LOCK_REASON = {
  location_spoofing: 'Reported a mock location',
};

const SessionGovernanceModule = () => {
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [locks, setLocks] = useState([]);
  const [loadingLocks, setLoadingLocks] = useState(true);
  const [unlocking, setUnlocking] = useState(null);
  const [unlockNote, setUnlockNote] = useState('');

  const [rules, setRules] = useState([]);
  const [loadingRules, setLoadingRules] = useState(true);
  const [savingRule, setSavingRule] = useState(false);
  const [form, setForm] = useState({ subject_kind: 'role', subject: '', max_active_sessions: '', note: '' });

  const search = useCallback(async (term) => {
    try {
      setSearching(true);
      const res = await sessionAPI.searchUsers({ q: term || undefined });
      setUsers(res?.data?.items || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to search users'));
    } finally {
      setSearching(false);
    }
  }, []);

  const loadRules = useCallback(async () => {
    try {
      setLoadingRules(true);
      const res = await sessionAPI.listRules();
      setRules(res?.data?.items || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load session rules'));
    } finally {
      setLoadingRules(false);
    }
  }, []);

  const loadLocks = useCallback(async () => {
    try {
      setLoadingLocks(true);
      const res = await accountLockAPI.list();
      setLocks(res?.data?.items || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load account locks'));
    } finally {
      setLoadingLocks(false);
    }
  }, []);

  useEffect(() => { search(''); loadRules(); loadLocks(); }, [search, loadRules, loadLocks]);

  const unlock = async (userId) => {
    if (unlockNote.trim().length < 3) {
      return toast.error('Say why you are unlocking — the note is required');
    }
    try {
      setUnlocking(userId);
      await accountLockAPI.unlock(userId, unlockNote.trim());
      toast.success('Account unlocked');
      setUnlockNote('');
      await loadLocks();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not unlock the account'));
    } finally {
      setUnlocking(null);
    }
  };

  const openUser = async (user) => {
    setSelected(user);
    try {
      setLoadingDetail(true);
      const res = await sessionAPI.listForUser(user.id);
      setDetail(res?.data || null);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load sessions'));
      setDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  const revoke = async (sessionId) => {
    try {
      await sessionAPI.revokeAny(sessionId);
      toast.success('Session revoked');
      if (selected) await openUser(selected);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not revoke session'));
    }
  };

  const saveRule = async () => {
    if (!form.subject.trim()) return toast.error(form.subject_kind === 'role' ? 'Enter a role key' : 'Enter a user ID');
    if (form.note.trim().length < 3) return toast.error('Explain the exception in the note — it is required');
    try {
      setSavingRule(true);
      await sessionAPI.upsertRule({
        subject_kind: form.subject_kind,
        subject: form.subject.trim(),
        // Blank means unlimited, matching the nullable column server-side.
        max_active_sessions: form.max_active_sessions === '' ? null : Number(form.max_active_sessions),
        note: form.note.trim(),
        is_active: true,
      });
      toast.success('Rule saved');
      setForm({ subject_kind: 'role', subject: '', max_active_sessions: '', note: '' });
      await loadRules();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not save rule'));
    } finally {
      setSavingRule(false);
    }
  };

  const deleteRule = async (ruleId) => {
    try {
      await sessionAPI.deleteRule(ruleId);
      toast.success('Rule deleted');
      await loadRules();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not delete rule'));
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* First on the page on purpose: a locked person cannot work, and every
          minute of that is a support call waiting to happen. */}
      <Card className="bg-card border-border">
        <CardHeader className="border-b border-border/50 bg-muted/20">
          <CardTitle className="text-foreground text-base flex items-center gap-2">
            <Lock className="h-4 w-4" /> Locked accounts
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Locked automatically when a device reported a mock location. Review the fraud
            flag first — a rooted phone is only ever flagged, never locked, so a lock here
            means a spoofing app was providing the location.
          </p>
        </CardHeader>
        <CardContent className="pt-4 space-y-3">
          {loadingLocks ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : locks.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">Nobody is locked out.</p>
          ) : (
            <>
              <div className="space-y-1">
                <Label>Unlock note (required)</Label>
                <Input
                  value={unlockNote}
                  onChange={(e) => setUnlockNote(e.target.value)}
                  placeholder="What you checked before unlocking — the next auditor reads this"
                />
              </div>
              {locks.map((lock) => (
                <div key={lock.id} className="rounded-lg border border-border p-3 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <MapPinOff className="h-4 w-4 text-red-500" />
                        <p className="text-sm font-medium text-foreground truncate">
                          {lock.name || lock.email || `User #${lock.user_id}`}
                        </p>
                        <Badge className="bg-red-500/15 text-red-600">locked</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {LOCK_REASON[lock.reason] || lock.reason}
                        {lock.employee_id ? ` · ${lock.employee_id}` : ''}
                        {' · '}{fmt(lock.locked_at)}
                        {lock.locked_by ? ` · by ${lock.locked_by}` : ''}
                      </p>
                      {lock.evidence?.device_id && (
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          Device {lock.evidence.device_id}
                          {(lock.evidence.integrity_signals || []).length
                            ? ` · signals: ${lock.evidence.integrity_signals.join(', ')}`
                            : ''}
                        </p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => unlock(lock.user_id)}
                      disabled={unlocking === lock.user_id}
                    >
                      {unlocking === lock.user_id
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <><Unlock className="h-4 w-4 mr-1" /> Unlock</>}
                    </Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Unlocking restores sign-in only. Their attendance for that day stays as
                    the system closed it — correct it through regularization if it is wrong.
                  </p>
                </div>
              ))}
            </>
          )}
        </CardContent>
      </Card>

      <Card className="bg-card border-border">
        <CardHeader className="border-b border-border/50 bg-muted/20">
          <CardTitle className="text-foreground text-base">Signed-in devices</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Pick a person to see every session their account has, and end any of them.
          </p>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          <div className="flex gap-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') search(query); }}
              placeholder="Search by email"
            />
            <Button variant="outline" onClick={() => search(query)} disabled={searching}>
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
          </div>

          <div className="grid gap-4 lg:grid-cols-[minmax(0,280px)_1fr]">
            <div className="space-y-1 max-h-80 overflow-y-auto pr-1">
              {users.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">No users matched.</p>
              ) : users.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => openUser(u)}
                  className={`w-full text-left rounded-lg border p-2 transition-colors ${
                    selected?.id === u.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'
                  }`}
                >
                  <p className="text-sm text-foreground truncate">{u.email}</p>
                  <p className="text-xs text-muted-foreground">
                    {u.role}{u.is_active ? '' : ' · disabled'}
                  </p>
                </button>
              ))}
            </div>

            <div>
              {!selected ? (
                <p className="text-sm text-muted-foreground py-4">Select a user to view their sessions.</p>
              ) : loadingDetail ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </div>
              ) : (
                <div className="space-y-3">
                  {detail?.policy && (
                    <div className="rounded-lg border border-border p-3 text-xs text-muted-foreground">
                      <span className="text-foreground font-medium">Policy: </span>
                      {detail.policy.enforced ? 'enforced' : 'not enforced'}
                      {' · '}
                      {detail.policy.max_active_sessions == null
                        ? 'unlimited devices'
                        : `${detail.policy.max_active_sessions} device(s)`}
                      {detail.policy.allow_kinds ? ` · ${detail.policy.allow_kinds.join(', ')}` : ''}
                      {detail.policy.source ? ` · from ${detail.policy.source}` : ''}
                      {detail.policy.reason ? ` — ${detail.policy.reason}` : ''}
                    </div>
                  )}

                  {(detail?.items || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground py-2">No sessions recorded.</p>
                  ) : (detail.items).map((s) => {
                    const Icon = KIND_ICON[s.device_kind] || HelpCircle;
                    return (
                      <div key={s.id} className="flex items-start justify-between gap-3 rounded-lg border border-border p-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4 text-muted-foreground" />
                            <p className="text-sm font-medium text-foreground truncate">{s.device_label}</p>
                            {s.is_live
                              ? <Badge className="bg-green-500/15 text-green-600">live</Badge>
                              : <Badge className="bg-muted text-muted-foreground">ended</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {s.client || 'Unknown client'}{s.ip_address ? ` · ${s.ip_address}` : ''}
                          </p>
                          <p className="text-[11px] text-muted-foreground mt-0.5">
                            Last seen {fmt(s.last_seen_at)} · started {fmt(s.created_at)}
                            {s.revoked_at ? ` · ended ${fmt(s.revoked_at)}${s.revoked_reason ? ` (${s.revoked_reason})` : ''}` : ''}
                          </p>
                        </div>
                        {s.is_live && (
                          <Button size="sm" variant="ghost" onClick={() => revoke(s.id)} title="Revoke">
                            <ShieldOff className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border">
        <CardHeader className="border-b border-border/50 bg-muted/20">
          <CardTitle className="text-foreground text-base">Device limits</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Exceptions to the default cap, by role or by individual. Leave the limit blank for unlimited.
          </p>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          <div className="grid gap-3 sm:grid-cols-4 items-end">
            <div className="space-y-1">
              <Label>Applies to</Label>
              <select
                value={form.subject_kind}
                onChange={(e) => setForm({ ...form, subject_kind: e.target.value, subject: '' })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="role">Role</option>
                <option value="user">User</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label>{form.subject_kind === 'role' ? 'Role key' : 'User ID'}</Label>
              <Input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder={form.subject_kind === 'role' ? 'e.g. hr_admin' : 'e.g. 42'}
              />
            </div>
            <div className="space-y-1">
              <Label>Max devices</Label>
              <Input
                type="number"
                min="1"
                max="50"
                value={form.max_active_sessions}
                onChange={(e) => setForm({ ...form, max_active_sessions: e.target.value })}
                placeholder="unlimited"
              />
            </div>
            <Button onClick={saveRule} disabled={savingRule}>
              {savingRule ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save rule</>}
            </Button>
          </div>
          <div className="space-y-1">
            <Label>Note (required)</Label>
            <Input
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="Why this exception exists — the next admin will read this"
            />
          </div>

          {loadingRules ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : rules.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">No exceptions — every role uses the workspace default.</p>
          ) : (
            <div className="space-y-2">
              {rules.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-2 rounded-lg border border-border p-3">
                  <div className="text-sm min-w-0">
                    <p className="text-foreground">
                      <span className="font-medium">{r.subject_kind === 'role' ? r.subject : `User #${r.subject}`}</span>
                      {' · '}
                      {r.max_active_sessions == null ? 'unlimited devices' : `${r.max_active_sessions} device(s)`}
                      {r.allow_kinds ? ` · ${r.allow_kinds.join(', ')}` : ''}
                      {r.is_active ? '' : ' · inactive'}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {r.note}{r.created_by ? ` — ${r.created_by}` : ''}
                    </p>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => deleteRule(r.id)} title="Delete rule">
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SessionGovernanceModule;
